import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { FormattedMessage, FormattedNumber, useIntl } from 'dibs-react-intl';
import Highlights from 'dibs-icons/exports/Highlights.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';
import LazyImage from 'dibs-react-lazy-image';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency.tsx';
import { getPriceByCurrency } from './currencyHelpers.ts';
import {
    HORIZONTAL_OFFSET_RESP_IMG,
    VERTICAL_OFFSET_RESP_IMG,
} from '../../constants/imageConstants.js';
import { getImageSrc } from 'dibs-buyer-layout/exports/imageHelpers.ts';
import { trackEvent, eventNameConstants } from 'dibs-tracking';

import { priceStatsMessages } from './sharedMessages.ts';

import cn from 'classnames';
import dibsCss from 'dibs-css';
import styles from './SbSharedMarketTrendsIconicDesigns.scss';

import { type SbSharedMarketTrendsIconicDesigns_creatorMarketTrends$key } from './__generated__/SbSharedMarketTrendsIconicDesigns_creatorMarketTrends.graphql.ts';

const creatorMarketTrendsFragment = graphql`
    fragment SbSharedMarketTrendsIconicDesigns_creatorMarketTrends on MarketTrendsConnection {
        topicDescription {
            isPlural
        }
        iconicDesigns {
            priceStats {
                valueChange
                avgSalePrice {
                    ...currencyHelpers_financialAmount
                }
                priceRange {
                    low {
                        ...currencyHelpers_financialAmount
                    }
                    high {
                        ...currencyHelpers_financialAmount
                    }
                }
            }
            iconicDesign {
                displayName
                urlLabel
                authorityImageUrl
            }
        }
    }
`;

type Props = {
    creatorMarketTrends: SbSharedMarketTrendsIconicDesigns_creatorMarketTrends$key;
    topic: string;
};

export const SbSharedMarketTrendsIconicDesigns: FC<Props> = ({
    creatorMarketTrends: creatorMarketTrendsRef,
    topic,
}) => {
    const { currency } = useCurrency();
    const { formatMessage } = useIntl();
    const creatorMarketTrends = useFragment(creatorMarketTrendsFragment, creatorMarketTrendsRef);
    const { iconicDesigns, topicDescription } = creatorMarketTrends || {};

    if (!iconicDesigns?.length) {
        return null;
    }

    return (
        <article className={dibsCss.mtMedium}>
            <header>
                <HeadingLevel>
                    {Heading => (
                        <Heading
                            className={cn(
                                dibsCss.sassyFontBodyMediumHeavy,
                                dibsCss.mt0,
                                dibsCss.mbXsmall
                            )}
                        >
                            <FormattedMessage
                                id="abf.SbSharedMarketTrends.iconicDesigns.header"
                                defaultMessage="Popular Designs and Their Prices"
                            />
                        </Heading>
                    )}
                </HeadingLevel>
            </header>
            <p className={cn(dibsCss.sassyFontBodyMedium, dibsCss.mbMedium)}>
                <FormattedMessage
                    id="abf.SbSharedMarketTrends.iconicDesigns.description"
                    defaultMessage="While {topic} {isPlural, select, true {have} other {has}} a broad price range, specific popular designs have their own market values. Here are a few notable examples:"
                    values={{
                        topic,
                        isPlural: !!topicDescription?.isPlural,
                    }}
                />
            </p>
            <div className={styles.wrapper}>
                <table className={cn(styles.iconicDesignsTable, dibsCss.textLeft, dibsCss.wFull)}>
                    <thead className={dibsCss.sassyFontBodySmallHeavy}>
                        <tr>
                            <th scope="col">
                                <FormattedMessage
                                    id="abf.SbRespMarketTrends.iconicDesigns.name"
                                    defaultMessage="Design Name"
                                />
                            </th>
                            <th scope="col">
                                {formatMessage(priceStatsMessages.averageSalePrice)}
                            </th>
                            <th scope="col">{formatMessage(priceStatsMessages.priceRange)}</th>
                            <th scope="col">{formatMessage(priceStatsMessages.valueChange)}</th>
                        </tr>
                    </thead>
                    <tbody className={dibsCss.sassyFontBodyMedium}>
                        {iconicDesigns.map(design => {
                            const { iconicDesign, priceStats } = design || {};
                            const { displayName, urlLabel, authorityImageUrl } = iconicDesign || {};
                            const { avgSalePrice, priceRange, valueChange } = priceStats || {};

                            const avgSalePriceAmount =
                                getPriceByCurrency(avgSalePrice, currency) ?? 0;
                            const priceRangeLowAmount =
                                getPriceByCurrency(priceRange?.low, currency) ?? 0;
                            const priceRangeHighAmount =
                                getPriceByCurrency(priceRange?.high, currency) ?? 0;

                            const authorityImageUrlSrc =
                                getImageSrc(authorityImageUrl ?? '', {
                                    width: 160,
                                }) ?? '';

                            return (
                                <tr key={urlLabel}>
                                    <td>
                                        <Link
                                            href={`/designs/${urlLabel}/`}
                                            className={styles.designLink}
                                            onClick={() => {
                                                trackEvent({
                                                    eventName:
                                                        eventNameConstants.EVENT_BROWSE_INTERACTION,
                                                    interaction_type:
                                                        'market trends - iconic designs click',
                                                });
                                            }}
                                        >
                                            {authorityImageUrlSrc ? (
                                                <div>
                                                    <LazyImage
                                                        offsetHorizontal={VERTICAL_OFFSET_RESP_IMG}
                                                        offsetVertical={HORIZONTAL_OFFSET_RESP_IMG}
                                                        placeholderClass={cn(
                                                            styles.designThumbnail,
                                                            dibsCss.roundedXxsmall
                                                        )}
                                                        placeholder={null}
                                                    >
                                                        <img
                                                            className={cn(
                                                                styles.designThumbnail,
                                                                dibsCss.roundedXxsmall
                                                            )}
                                                            src={authorityImageUrlSrc}
                                                            alt={displayName || ''}
                                                        />
                                                    </LazyImage>
                                                </div>
                                            ) : (
                                                <div
                                                    className={cn(
                                                        styles.designThumbnailPlaceholder,
                                                        dibsCss.roundedXxsmall,
                                                        dibsCss.bgWhite,
                                                        dibsCss.flex,
                                                        dibsCss.itemsCenter,
                                                        dibsCss.justifyCenter,
                                                        dibsCss.fillDolphin
                                                    )}
                                                >
                                                    <Highlights variation="40Filled" />
                                                </div>
                                            )}

                                            <span className={styles.designName}>
                                                <strong>{displayName}</strong>
                                            </span>
                                        </Link>
                                    </td>
                                    <td>
                                        <FormattedNumber
                                            value={avgSalePriceAmount}
                                            style="currency"
                                            currency={currency}
                                            maximumFractionDigits={0}
                                        />
                                    </td>
                                    <td>
                                        <FormattedNumber
                                            value={priceRangeLowAmount}
                                            style="currency"
                                            currency={currency}
                                            maximumFractionDigits={0}
                                        />
                                        {' – '}
                                        <FormattedNumber
                                            value={priceRangeHighAmount}
                                            style="currency"
                                            currency={currency}
                                            maximumFractionDigits={0}
                                        />
                                    </td>
                                    <td>{`${valueChange}%`}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </article>
    );
};
